import { ref, nextTick, type Ref } from 'vue';

/**
 * Interface for AccordionGroup
 * This allows for dynamic management of accordion groups and their open/closed states.
 * @property {string} id - Unique identifier for the accordion group.
 * @property {any[]} items - Array of items in the accordion group.
 * @property {boolean} [hidden] - Optional property to hide the group.
 */
export interface AccordionGroup {
  id: string;
  items: any[];
  hidden?: boolean;
}

export function useAccordionTabs<T extends AccordionGroup>(groups: Ref<T[]>) {
  // Accordion state management - stores which tabs are open for each group
  const openAccordionTabs = ref<Record<string, number[]>>({});

  /**
   * Initialize accordion tabs - set all tabs to be open by default
   */
  const initializeAccordionTabs = () => {
    const newOpenTabs: Record<string, number[]> = {};
    // Iterate through each group and set all items as open if the group is not hidden
    groups.value.forEach((group) => {
      if (!group.hidden && group.items) {
        newOpenTabs[group.id] = group.items.map((_, index) => index);
      }
    });
    
    // Merge with existing state to preserve user interactions
    openAccordionTabs.value = { ...openAccordionTabs.value, ...newOpenTabs };
  };

  /**
   * Reset all accordion tabs to be open
   */
  const resetAccordionTabs = () => {
    nextTick(() => {
      initializeAccordionTabs();
    });
  };

  /**
   * Handle accordion tab state change for a specific group
   */
  const handleAccordionChange = (groupId: string, openTabs: number[]) => {
    openAccordionTabs.value[groupId] = openTabs;
  };

  /**
   * Initialize accordion tabs with a delay for reactivity
   */
  const initializeWithDelay = () => {
    nextTick(() => {
      initializeAccordionTabs();
    });
  };

  /**
   * Get open tabs for a specific group
   */
  const getOpenTabs = (groupId: string): number[] => {
    return openAccordionTabs.value[groupId] || [];
  };

  /**
   * Check if accordion tabs are initialized for a specific group
   */
  const isInitialized = (groupId: string): boolean => {
    return openAccordionTabs.value[groupId] !== undefined;
  };

  /**
   * Set specific tabs as open for a group
   */
  const setOpenTabs = (groupId: string, tabIndices: number[]) => {
    openAccordionTabs.value[groupId] = tabIndices;
  };

  /**
   * Open all tabs for a specific group
   */
  const openAllTabs = (groupId: string) => {
    const group = groups.value.find(g => g.id === groupId);
    if (group && group.items) {
      openAccordionTabs.value[groupId] = group.items.map((_, index) => index);
    }
  };

  /**
   * Close all tabs for a specific group
   */
  const closeAllTabs = (groupId: string) => {
    openAccordionTabs.value[groupId] = [];
  };

  return {
    openAccordionTabs,
    initializeAccordionTabs,
    resetAccordionTabs,
    handleAccordionChange,
    initializeWithDelay,
    getOpenTabs,
    isInitialized,
    setOpenTabs,
    openAllTabs,
    closeAllTabs
  };
}
